<template id="document-form">
    <form id="create-document-form" v-if="document">
        <h3 class="m-t-0">{{ trans('documents.document_create') }} <span v-if="document.document_template">({{ document.document_template.name }})</span></h3>

        <div class="flex flex-col row sm:flex-row">
            <div class="col-xs-12 col-sm-5">
                <div class="form-group" :class="{ 'has-error' : errors.has('name') }">
                    <label class="control-label">{{ trans('documents.document_title_label') }}</label>
                    <input name="create-title" type="text" class="form-control" v-model="document.name">
                    <span class="help-block" v-if="errors.has('name')">{{ errors.first('name') }}</span>
                </div>
            </div>

            <div class="col-xs-12 col-sm-2">
                <div class="form-group" :class="{ 'has-error' : errors.has('date') }">
                    <label class="control-label">{{ trans('documents.document_date_label') }}</label>
                    <date-picker name="date" v-model="document.date" />
                    <span v-show="errors.has('date')" class="help-block m-l-5" style="display:inline;">{{ errors.first('date') }}</span>
                </div>
            </div>

            <div class="col-xs-12 col-sm-5">
                <div class="form-group">
                    <template v-if="!document.document_template">
                        <label class="control-label">{{ trans('documents.document_template_choose') }}</label>
                        <select v-model="document.document_template" class="form-control" name="document_template">
                            <option :value="0">{{ trans('client.journal_template') }}</option>
                            <option v-for="(template, index) in documentTemplates" :key="index" :value="template">{{ template.name }}</option>
                        </select>
                    </template>
                    <template v-else>
                        <label class="control-label">{{ trans('documents.document_template_locked') }}</label>
                        <select class="form-control" :disabled="true">
                            <option>{{ document.document_template.name }}</option>
                        </select>
                    </template>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-xs-12" v-if="is_simple_document">
                <div class="form-group" :class="{'has-error': errors.has('content')}">
                    <label class="m-r-5 control-label">{{ trans('documents.document_content_label') }}</label>

                    <rich-text-editor
                        :oembedSupport="true"
                        :id="richTextEditorId"
                        name="create-content"
                        v-model="document.content"
                        :config="contentDocumentEditorConfig"
                        uploadResourceType="document"
                    ></rich-text-editor>
                    <span class="help-block" v-if="errors.has('content')">{{ errors.first('content') }}</span>
                </div>
            </div>

            <div class="col-xs-12" v-if="is_advanced_document">
                <div class="form-group" :class="{'has-error': errors.has('content')}">
                    <label class="m-r-5 control-label">{{ trans('documents.document_content_label') }}</label>

                    <advanced-document-form
                        v-model="document.content"
                        :template="document.document_template"
                        :client="client"
                        @updatedPdfData="(data) => pdfData = data">
                    </advanced-document-form>
                    <span class="help-block" v-if="errors.has('content')">{{ errors.first('content') }}</span>
                </div>
            </div>

            <div class="col-xs-12 buttons">
                <ep-button type="submit" start-icon="save" class="pull-right m-l-5" :disabled="save_button_disabled" @click.prevent="submitDocument">{{ trans('global.save_and_close') }}</ep-button>
                <ep-button start-icon="xmark" variant="tertiary" class="pull-left m-r-10" :disabled="loading" @click.prevent="cancelDocument">{{ trans('global.cancel') }}</ep-button>
            </div>
        </div>
    </form>
</template>

<script type="text/javascript">
import { http } from '@/system/http'
import moment from 'moment'
import DatePicker from '@/system/components/global/form/DatePicker.vue'
import AdvancedDocumentForm from 'Components/global/AdvancedDocumentForm.vue'
import replaceContentTags from '@/system/helpers/replaceContentTags.js'

export default {
    components: {
        DatePicker,
        AdvancedDocumentForm,
    },

    props: {
        client: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            loading: false,
            document: null,
            pdfData: {},
            richTextEditorId: 'create-document-content-editor',
            contentDocumentEditorConfig: {
                toolbar: [
                    'undo', 'redo',
                    '|',
                    'bold', 'italic', 'underline', 'strikethrough',
                    '|',
                    'numberedList', 'bulletedList',
                    '|',
                    'alignment',
                    '|',
                    'heading',
                    '|',
                    'link',
                    '|',
                    'insertTable',
                    'imageUpload',
                ],
            },
        }
    },

    computed: {
        is_simple_document() {
            return this.document.document_type === 'simple'
        },

        is_advanced_document() {
            return this.document.document_type === 'advanced'
        },

        documentTemplates() {
            return this.$store.getters['documents/getTemplatesByType'](this.document.document_type)
        },

        save_button_disabled() {
            if (this.loading) return true

            if (this.is_advanced_document && !this.document.document_template) return true

            return false
        }
    },

    methods: {
        setDefaultDocument(type) {
            this.document = {
                content: type === 'simple' ? '' : {},
                name: '',
                document_type: type,
                document_template: 0,
                date: moment().format("DD-MM-YYYY"),
                client_id: this.client ? this.client.id : null,
            };
        },

        selectDocumentTemplate(template) {
            if (!template) return;

            const tagReplacements = this.$store.getters['journals/getTagReplacementsForClientOrGroup'](this.client, false);

            this.document.content = replaceContentTags(template.content, tagReplacements);
        },

        submitDocument() {
            this.loading = true

            http.post('/app/documents', {...this.document, ...this.pdfData})
                .then(({ data }) => {
                    this.$event_bus.$emit('documentCreated', data.document)
                    this.errors.clear()
                    this.loading = false
                    toastr.success(data.success)
                    this.document = data.document
                    this.$event_bus.$emit('closeCreateNewDocument')
                })
                .catch(({ error, errorBag }) => {
                    this.loading = false
                    this.$setErrorBag(errorBag)
                });
        },

        cancelDocument() {
            this.loading = false
            this.errors.clear()
            this.$event_bus.$emit('closeCreateNewDocument')
        },
    },

    watch: {
        'document.document_template'(val) {
            if (val) {
                if (this.is_advanced_document) {
                    this.errors.clear()

                    this.$event_bus.$emit('templateSelected', val)

                    return
                }

                this.selectDocumentTemplate(val)
            }
        }
    },

    mounted() {
        this.$store.dispatch('documents/loadTemplates');

        this.$event_bus.$on('createNewDocument', (type) => {
            this.setDefaultDocument(type)
        });

        this.$event_bus.$on('closeCreateNewDocument', () => {
            this.pdfData = {}
            this.errors.clear()
            this.setDefaultDocument()
        })
    },
}
</script>
